import { endOfMonth, format, isValid, parseISO, startOfDay, startOfMonth } from 'date-fns';

/**
 * BOPS-115 — shared rules for custom closeout periods.
 *
 * A custom period is identified on the wire by its exact date range; there is no uuid. Every rule
 * below is also enforced server side — these client checks exist to give immediate feedback, not
 * to be the source of truth. Overlap is deliberately NOT checked here: the backend names the
 * conflicting period in its message, which this component can't do for months it hasn't loaded.
 */

/**
 * How far back closeouts reach — the Weekly / Monthly Years dropdown, the Custom range month
 * picker, and the New closeout period date pickers alike.
 *
 * The dropdown was three hardcoded years, which both hid every older closeout and needed editing
 * each January. It is derived from the current year now, and floored here rather than left open:
 * a year with no closeouts behind it is only a wasted click, and a period created below the floor
 * would be invisible to every control that lists one.
 */
export const EARLIEST_CLOSEOUT_YEAR = 2000;

export const CUSTOM = 'CUSTOM';
export const WEEKLY = 'WEEKLY';
export const MONTHLY = 'MONTHLY';

/** The API takes plain yyyy-MM-dd, never an ISO instant — a timezone would shift the day. */
export const toApiDate = (date: Date): string => format(date, 'yyyy-MM-dd');

export const formatRange = (startDate?: string, endDate?: string): string =>
  `${format(new Date(startDate ?? ''), 'MMM d, yyyy')} - ${format(
    new Date(endDate ?? ''),
    'MMM d, yyyy'
  )}`;

// -------------------------------------------------------------------------------------------------
//  Sign Certification modal (BOPS-115 / BOPS-118 mock)
// -------------------------------------------------------------------------------------------------

/**
 * The same modal signs all three close types, so every sentence in it names the scope rather than
 * assuming a month. Anything unrecognised falls back to the neutral "range" wording.
 */
export const closeoutScopeLabel = (division?: string): string =>
  division === CUSTOM ? 'Custom range' : division === MONTHLY ? 'Monthly' : 'Weekly';

export const closeoutScopeNoun = (division?: string): string =>
  division === MONTHLY ? 'month' : division === WEEKLY ? 'week' : 'range';

/**
 * "May 20, 2026 – May 31, 2026" (en dash, per the mock). Returns null rather than "Invalid Date"
 * when the row has no usable range — the caller drops the chip entirely.
 */
export const formatSignRange = (startDate?: string, endDate?: string): string | null => {
  if (!startDate || !endDate) return null;
  const from = parseISO(startDate);
  const to = parseISO(endDate);
  if (!isValid(from) || !isValid(to)) return null;
  return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
};

/** "0 Errors" / "1 Error" — the count reads as a sentence, so it has to pluralise. */
export const alertCountLabel = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** "Aug 1" — the year is redundant inside a range that can't leave one month. */
const shortDate = (date: Date) => format(date, 'MMM d');

const isSameCalendarMonth = (from: Date, to: Date) =>
  from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth();

const isFullCalendarMonth = (from: Date, to: Date) =>
  from.getDate() === startOfMonth(from).getDate() && to.getDate() === endOfMonth(to).getDate();

/**
 * BOPS-115 (8/7 decision) — a custom period must start and end today or in the past.
 *
 * Comparison is day-granular on purpose: a picker hands back midnight, but a range that ends
 * "today" is legitimate, so anything finer would reject the one date the rule explicitly allows.
 */
export const isFutureDay = (date: Date, now: Date = new Date()): boolean =>
  startOfDay(date) > startOfDay(now);

/** The last day a custom period may cover — today, never later. */
export const latestSelectableDay = (now: Date = new Date()): Date => startOfDay(now);

/**
 * The first day a custom period may cover — the same floor the year filters use, so a period
 * can't be created for a date the pickers that list it could never reach afterwards.
 */
export const earliestSelectableDay = (): Date => startOfDay(new Date(EARLIEST_CLOSEOUT_YEAR, 0, 1));

const isBeforeFloor = (date: Date): boolean => startOfDay(date) < earliestSelectableDay();

/**
 * Returns a user-facing reason the range can't be used, or null when it's acceptable.
 * Mirrors MonthEndAlertsCloseOutServiceImpl.validateCustomRange minus the overlap check.
 */
export const validateCustomRange = (from: Date | null, to: Date | null): string | null => {
  if (!from || !to || !isValid(from) || !isValid(to)) {
    return 'Select both a From and a To date.';
  }
  if (from > to) {
    return 'The From date must be on or before the To date.';
  }
  //  Named before the month rules: a date that hasn't happened is the root problem, and pointing at
  //  the calendar month of a range the user can't close either way would send them the wrong way.
  //  From is reported when it is itself in the future — it's the date they have to move first.
  if (isFutureDay(from) || isFutureDay(to)) {
    const offending = isFutureDay(from) ? from : to;
    return `${format(
      offending,
      'MMM d, yyyy'
    )} is in the future. A closeout period must start and end today or in the past — you can't close through a date that hasn't happened yet (today is ${format(
      new Date(),
      'MMM d, yyyy'
    )}).`;
  }
  //  Same reasoning as the future check above: an out-of-range endpoint is the root problem, so
  //  it is named before the month rules rather than after them. From is reported first when both
  //  are too early, because it is the date the user has to move.
  if (isBeforeFloor(from) || isBeforeFloor(to)) {
    const offending = isBeforeFloor(from) ? from : to;
    return `${format(offending, 'MMM d, yyyy')} is before ${format(
      earliestSelectableDay(),
      'MMM d, yyyy'
    )}. Closeout periods can't be created for dates that early.`;
  }
  if (!isSameCalendarMonth(from, to)) {
    return 'A closeout period must sit within one calendar month.';
  }
  if (isFullCalendarMonth(from, to)) {
    return `${shortDate(from)} – ${shortDate(
      to
    )} is a full calendar month. Close it from the Monthly tab instead — a whole month is a monthly close, not a custom one.`;
  }
  return null;
};

/**
 * Edit and Cancel are offered only before a period's first close & sign. The list response carries
 * no closeoutDate, but it doesn't need to: a period that has ever been closed is CLOSED_AND_SIGNED
 * or REOPEN, never OPEN — which is exactly the ticket's "both disappear once closed, even after
 * reopen".
 */
export const isNeverClosed = (row: any): boolean => row?.status === 'OPEN';

/**
 * BOPS-118 (Olivia, 10 Aug) — the signer is shown on the **hard** closeouts only: monthly and
 * custom. Weekly is excluded, and excluded here rather than left to the API: the backend not
 * sending `signedByName` for a weekly row is the reason nothing renders today, which makes the
 * scope rule invisible in this codebase and one backend change away from breaking.
 */
export const isHardCloseout = (division?: string): boolean =>
  division === MONTHLY || division === CUSTOM;

/**
 * BOPS-118 — "Closed & signed by name", with the date it was closed.
 *
 * The signer is always recorded on close, but the API only hands it over while the BOPS-118 toggle
 * is on. Presence of the name is therefore the toggle's whole render condition — there is no
 * separate frontend flag — and the division decides whether this close type shows it at all.
 *
 * The date is optional on purpose: an older row closed before the field existed still names its
 * signer, which is more useful than dropping the line for want of a timestamp.
 */
export const hasSignerData = (row: any, division?: string): boolean =>
  isHardCloseout(division) &&
  typeof row?.signedByName === 'string' &&
  row.signedByName.trim() !== '';

export const signerLine = (row: any, division?: string): string | null => {
  if (!hasSignerData(row, division)) return null;
  const name = row.signedByName.trim();

  const closedOn = row?.closeoutDate ? parseISO(row.closeoutDate) : null;
  return closedOn && isValid(closedOn)
    ? `Closed & signed by ${name} · ${format(closedOn, 'MMM d, yyyy')}`
    : `Closed & signed by ${name}`;
};

// -------------------------------------------------------------------------------------------------
//  Server message → user-facing copy
// -------------------------------------------------------------------------------------------------

const QUOTED_ISO_DATE = /'(\d{4}-\d{2}-\d{2})'/g;

const prettyDate = (iso: string) => format(parseISO(iso), 'MMM d, yyyy');
const prettyMonth = (iso: string) => format(parseISO(iso), 'MMMM yyyy');
const shortIso = (iso: string) => format(parseISO(iso), 'MMM d');

/**
 * BOPS-115 — the backend rejects a bad range with a developer-facing string: raw ISO dates wrapped
 * in single quotes, and wording aimed at a log rather than a user ("got '2026-10-09' to
 * '2026-10-23'"). Per the design-system copy rules those must never reach the screen, but the
 * message is also the only place that knows *which* period conflicts — so rewrite rather than
 * replace, keeping the specifics and dropping the machine formatting.
 *
 * Anything unrecognised still gets its dates humanised, so a new server message degrades into
 * readable English instead of leaking quoted ISO strings.
 */
export const humanizeCloseoutError = (
  raw?: string | null,
  //  The overlap advice differs by surface: creating suggests closing the remaining days as their
  //  own period, editing suggests moving this one's start date (per the mocks).
  context: 'create' | 'edit' = 'create'
): string | null => {
  if (!raw) return null;

  const overlap = raw.match(
    /Range '(\d{4}-\d{2}-\d{2})' to '(\d{4}-\d{2}-\d{2})' overlaps the existing custom closeout period '(\d{4}-\d{2}-\d{2})' to '(\d{4}-\d{2}-\d{2})'/
  );
  if (overlap) {
    return `${shortIso(overlap[1])} – ${shortIso(
      overlap[2]
    )} overlaps an existing closeout period (${shortIso(overlap[3])} – ${shortIso(overlap[4])}). ${
      context === 'edit'
        ? "Pick a start date that doesn't overlap another period."
        : "Custom closeout periods can't overlap — close the remaining days as their own period instead."
    }`;
  }

  const monthlyClosed = raw.match(
    /Month '(\d{4}-\d{2}-\d{2})' has already been closed from the Monthly tab/
  );
  if (monthlyClosed) {
    const month = prettyMonth(monthlyClosed[1]);
    return `${month} has already been closed with a monthly closeout, so you can't create a custom closeout that overlaps it — reopening the month doesn't change that. To adjust ${
      month.split(' ')[0]
    }, use the Monthly tab.`;
  }

  const managedByCustom = raw.match(
    /Month '(\d{4}-\d{2}-\d{2})' is managed on the Custom range tab/
  );
  if (managedByCustom) {
    return `${prettyMonth(
      managedByCustom[1]
    )} is already managed on the Custom range tab. Close the remaining dates as custom periods.`;
  }

  const fullMonth = raw.match(
    /Range '(\d{4}-\d{2}-\d{2})' to '(\d{4}-\d{2}-\d{2})' covers a whole calendar month/
  );
  if (fullMonth) {
    return `${shortIso(fullMonth[1])} – ${shortIso(
      fullMonth[2]
    )} is a full calendar month. Close it from the Monthly tab instead — a whole month is a monthly close, not a custom one.`;
  }
  if (/covers a whole calendar month/.test(raw)) {
    return 'That range covers a whole calendar month. Close it from the Monthly tab instead — a whole month is a monthly close, not a custom one.';
  }

  if (/sit within one calendar month/.test(raw)) {
    return 'A closeout period must sit within one calendar month.';
  }

  //  The verb is the backend's ("edited" / "cancelled"), so the sentence names the action the user
  //  just attempted rather than listing both.
  const alreadyClosed = raw.match(
    /has already been closed and signed, so it can no longer be (\w+)/
  );
  if (alreadyClosed) {
    return `This period has already been closed and signed, so it can no longer be ${alreadyClosed[1]}. It can only be reopened.`;
  }

  if (/auto-generated and cannot be cancelled/.test(raw)) {
    return 'Weekly and monthly closeouts are generated automatically and cannot be cancelled.';
  }

  return raw.replace(QUOTED_ISO_DATE, (_match, iso) => prettyDate(iso));
};
